import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import posthog from 'posthog-js';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { UiMode } from 'src/assets/data/enum/uiMode';
import {
  EventTracker,
  EventTrackerAvailableSection,
  EventTrackerClickMapper,
  EventTrackerModulesMapper,
} from '../../../assets/data/enum/eventTracker';
import { GeneralUtilsService } from './common/generalUtils.service';
import { PosthogService } from './posthog.service';
import { isLocalhost } from '../../../main';

type Mode = 'agro' | 'livestock';

const SECTION_SESSION_START = 'section_session_start';
const SECTION_SESSION_END = 'section_session_end';

interface PostHogEventProperties {
  mode: Mode;
  section: EventTrackerAvailableSection;
  start_timestamp?: string;
  end_timestamp?: string;
  duration_seconds?: number;
}

export interface UserInfo {
  email: string;
  country: string;
  language: string;
  tracking_product: boolean;
  tracking_marketing: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class EventTrackerService implements OnDestroy {
  private router = inject<Router>(Router);
  private posthogService = inject(PosthogService);

  private subscribers: Subscription = new Subscription();

  private generalUtilsService = inject(GeneralUtilsService);

  private modeUI = toSignal(this.generalUtilsService.globalUIMode);
  private eventMode = computed(() => (this.modeUI() === UiMode.FARM ? 'agro' : 'livestock'));

  eventTrackingActivated = signal(false);
  currentModule = signal<PostHogEventProperties>(null);

  constructor() {
    if (!this.isEnabled()) {
      return;
    }

    // This event is observed here to ensure that all modules are tracked only once and exclusively when they are activated.
    const routerSub = combineLatest([
      this.generalUtilsService.globalUIMode,
      this.router.events.pipe(
        filter((event) => event instanceof NavigationStart)
      ) as BehaviorSubject<NavigationStart>,
    ]).subscribe(([_, routerEvent]) => {
      if (!this.isEnabled()) {
        return;
      }

      this.trackModuleEvent(routerEvent.url);
    });

    this.subscribers.add(routerSub);
  }

  trackModuleEvent(url: string) {
    const moduleUrl = this.getModuleUrl(url);

    // if somehow module url doesn't exist
    if (!moduleUrl) {
      return;
    }

    const section = EventTrackerModulesMapper[this.modeUI()][moduleUrl];

    // if module/section is not tracked
    if (!section) {
      this.endLastSessionIfExist(new Date().toString());
      this.currentModule.set(null);
      return;
    }

    const mode = this.eventMode();
    // if the user is already in the same module, do nothing
    if (this.currentModule()?.section === section && this.currentModule()?.mode === mode) {
      return;
    }

    const start_timestamp = new Date().toString();
    this.endLastSessionIfExist(start_timestamp);
    this.trackEvent(SECTION_SESSION_START, { mode, section, start_timestamp });

    this.currentModule.set({ mode, section, start_timestamp });
  }

  /**
   * Track on PostHog a custom event.
   * @param eventName - Name of the custom event to track.
   * @param properties - Additional Properties of the event to be tracked. If not provided we assume it's a click event and we get the properties from the EventTrackerClickMapper.
   */
  trackEvent(eventName: string, properties?: PostHogEventProperties) {
    if (!eventName) return;
    const { mode, section, start_timestamp } =
      properties ?? this.getClickEventProperties(eventName);

    if (this.isEnabled() && this.eventTrackingActivated()) {
      this.posthogService.captureEvent(eventName, {
        mode,
        section,
        start_timestamp,
        ...properties,
      });
    }
  }

  /**
   * Description - Reset the PostHog connection.
   */
  reset(): void {
    if (!this.isEnabled()) {
      return;
    }

    this.eventTrackingActivated.set(false);
    this.posthogService.reset();
  }

  /**
   * Description - Subscribe to tab closed event to notify it.
   */
  registerTabCloseEvent(): void {
    if (!this.isEnabled()) {
      return;
    }

    const onPageHide = (): void => {
      const payload = {
        event: EventTracker.CLOSE_APPLICATION_WEB,
        properties: {},
        distinct_id: posthog.get_distinct_id(),
        timestamp: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });

      navigator.sendBeacon(`${environment.eventTrackingEndpoint}/e/`, blob);
    };

    window.addEventListener('pagehide', onPageHide);
  }

  isEnabled() {
    return environment.postHogEnabled && !isLocalhost;
  }

  private getClickEventProperties(event: string) {
    const mode = this.eventMode();
    const section = EventTrackerClickMapper[event];
    const start_timestamp = new Date().toString();
    return { mode, section, start_timestamp };
  }

  private getModuleUrl(url: string) {
    const afterAuth = url.split('auth/')[1];
    if (!afterAuth) {
      return '';
    }
    // special cases
    if (
      afterAuth.split('/')[0] === 'company-data' ||
      afterAuth === 'satellite/manage-prescription' ||
      afterAuth === 'satellite/manage-files'
    ) {
      return afterAuth;
    }

    return afterAuth.split('/')[0];
  }

  ngOnDestroy() {
    this.subscribers.unsubscribe();
  }

  private endLastSessionIfExist(timestamp: string) {
    if (!this.currentModule()) {
      return;
    }
    const date = new Date(timestamp);
    const startDate = new Date(this.currentModule().start_timestamp);
    const durationSeconds = Math.round((date.getTime() - startDate.getTime()) / 1000) || 0;

    const endEventProperties: PostHogEventProperties = {
      ...this.currentModule(),
      end_timestamp: timestamp,
      duration_seconds: durationSeconds,
    };

    this.trackEvent(SECTION_SESSION_END, endEventProperties);
  }
}
